import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import ReactTooltip from 'react-tooltip'
import Select from 'react-select'
import { Formik } from 'formik'
import { Note, ArrowUpRight, Info } from '@phosphor-icons/react'

import RadioField from '../../../FormLegacy/FormFieldsV2/RadioField'
import DateField from '../../../FormLegacy/FormFieldsV2/DateField'
import CheckboxField from '../../../FormLegacy/FormFieldsV2/CheckboxField'
import SelectField from '../../../FormLegacy/FormFieldsV2/SelectField'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import {
  TRIGGER_OPTIONS,
  TRIGGER_TYPES,
  MAX_STAFF_OPTIONS,
  fetchAllUnitOptions,
  getInspectionFrequencyFields,
  getInspectionLocationInput,
  getInspectionData,
  getInspectionsListPath,
} from '../../helpers'
import {
  LOCATION_TYPES,
  getMultiUnitCautionText,
  isLocationValueFilled,
  toUnitArray,
} from '../../unitSelection'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import { selectStaffOptions } from '../../../../reducers/maintenanceStaffReducer'
import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import { selectPropertyManagerOptions } from '../../../../reducers/Organisation/propertyManagersReducer'
import {
  createInspectionFromTemplate,
  fetchScheduledInspections,
} from '../../../../reducers/inspectionsReducer'
import API from '../../../../api/api'

const CreateInspectionFromTemplate = ({
  onClose = () => {},
  onBack = () => {},
  createInspectionFromTemplate = () => {},
  fetchScheduledInspections = () => {},
  selectedTemplate,
  currentTrigger,
  staffOptions,
  propertyManagerOptions,
  building,
}) => {
  const { t } = useTranslation(['internal-inspections', 'common'])
  const [yardiOptions, setYardiOptions] = useState([])
  const [unitTypes, setUnitTypes] = useState([])
  const [unitOptions, setUnitOptions] = useState([])
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const { displaySnackbar } = useSnackbar()
  const navigate = useNavigate()

  const isYardi = building && !!building.yardiPropertyId
  const isRentManager = building && !!building.rentManagerPropertyId

  const isIntegratedWith = isYardi || isRentManager
  const fetchYardiPropertyIds = async () => {
    try {
      const { data } = await API.getYardiPropertyIds(building.id)
      if (data) {
        const options = data.map(({ yardiPropertyId }) => ({
          value: yardiPropertyId,
          label: yardiPropertyId,
        }))
        setYardiOptions(options)
      }
    } catch ({ message }) {
      console.error(message)
    }
  }

  const fetchUnitTypes = async () => {
    try {
      const { data } = await API.getUnitTypes(building.id)
      if (data) {
        const options = data.map((name, index) => ({
          id: index + 1,
          text: name,
          name,
        }))
        setUnitTypes(options)
      }
    } catch ({ message }) {
      console.error(message)
    }
  }

  const fetchAllUnits = async (phase = '') => {
    if (!building) return
    setUnitOptions(await fetchAllUnitOptions(building.id, phase))
  }

  const createInspection = async (values) => {
    try {
      const requestData = getInspectionData(values, building)
      requestData.templateId = selectedTemplate.value

      await createInspectionFromTemplate(requestData)
      displaySnackbar(t('internal-inspections:createInspectionFromTemplate.scheduledSuccessfully'))
      await fetchScheduledInspections({ buildingId: building.id })
      onClose()
      navigate(getInspectionsListPath(currentTrigger))
    } catch ({ message }) {
      displaySnackbar(message, true)
    }
  }

  const canSubmit = (values) =>
    isLocationValueFilled(values.locationValue) &&
    (values.frequency === TRIGGER_TYPES.TIME
      ? !!values.date
      : !!values.triggerType && !!values.triggerDays && !!values.triggerCondition)

  const getSelectedUnitsCount = (values) =>
    values.locationType === LOCATION_TYPES.UNIT ? toUnitArray(values.locationValue).length : 0

  const onChangeLocationType = (value, values, setValues) => {
    setValues({
      ...values,
      locationType: value,
      locationValue: value === LOCATION_TYPES.UNIT || value === LOCATION_TYPES.UNIT_TYPES ? [] : '',
    })
  }

  useEffect(() => {
    if (building) {
      fetchAllUnits()
    }
    if (isIntegratedWith) {
      if (isYardi) {
        fetchYardiPropertyIds()
      }
      fetchUnitTypes()
    }
  }, [isIntegratedWith, building])

  return (
    <Formik
      initialValues={{
        locationType: LOCATION_TYPES.UNIT,
        locationValue: [],
        frequency: TRIGGER_TYPES.TIME,
        recurrenceType:
          TRIGGER_OPTIONS.ONE_TIME === currentTrigger ? TRIGGER_OPTIONS.ONE_TIME : 'weekly',
        dayOfWeek: null,
        date: null,
        recurrenceEndDate: null,
        assigneeIds: [],
        triggerDays: 1,
        triggerType: 'move-in',
        triggerCondition: 'before',
        reviewRequired: false,
      }}
      onSubmit={createInspection}
    >
      {({ values, isSubmitting, handleChange, setFieldValue, handleSubmit, setValues }) => (
        <React.Fragment>
          <div className="inspections-modal-content create-inspection-from-template">
            <div className="selected-template">
              <div>
                <Note />
                <div>
                  <span>
                    {t('internal-inspections:createInspectionFromTemplate.templateSelected')}
                  </span>
                  <span>{selectedTemplate.label}</span>
                </div>
              </div>
              <a
                href={`#/dashboard/building-dashboard/internal-inspections/template/view/${selectedTemplate.value}`}
                target="_blank"
                className="button button-gray"
              >
                {t('internal-inspections:templateNavbar.viewTemplate')}
                <ArrowUpRight />
              </a>
            </div>
            <div>
              <span className="input-label">
                {t('internal-inspections:createInspectionFromTemplate.selectLocation')}
              </span>
              <div className="radio-buttons-container">
                <div className="radio-buttons-label">
                  <span className="input-label--small">
                    {t('internal-inspections:createInspectionFromTemplate.single')}
                  </span>
                  {unitTypes.length > 0 && (
                    <React.Fragment>
                      <span className="input-label--small">
                        {t('internal-inspections:createInspectionFromTemplate.locationGroup')}{' '}
                        <Info data-tip data-for="location-group" />
                      </span>
                      <ReactTooltip
                        className="tooltip"
                        id="location-group"
                        place="top"
                        effect="solid"
                      >
                        <div>
                          {t(
                            'internal-inspections:createInspectionFromTemplate.locationGroupTooltip',
                          )}
                        </div>
                      </ReactTooltip>
                    </React.Fragment>
                  )}
                </div>
                <div className="radio-buttons">
                  <RadioField
                    label={t('internal-inspections:createInspectionFromTemplate.unit')}
                    name="locationType"
                    value={LOCATION_TYPES.UNIT}
                    checked={values.locationType === LOCATION_TYPES.UNIT}
                    onChange={() => onChangeLocationType(LOCATION_TYPES.UNIT, values, setValues)}
                  />
                  <RadioField
                    label={t('internal-inspections:createInspectionFromTemplate.commonArea')}
                    name="locationType"
                    value={LOCATION_TYPES.COMMON_AREA}
                    checked={values.locationType === LOCATION_TYPES.COMMON_AREA}
                    onChange={() =>
                      onChangeLocationType(LOCATION_TYPES.COMMON_AREA, values, setValues)
                    }
                  />
                  <RadioField
                    label={t('internal-inspections:createInspectionFromTemplate.other')}
                    name="locationType"
                    value={LOCATION_TYPES.OTHER}
                    checked={values.locationType === LOCATION_TYPES.OTHER}
                    onChange={() => onChangeLocationType(LOCATION_TYPES.OTHER, values, setValues)}
                  />
                  {unitTypes.length > 0 && (
                    <RadioField
                      label={t('internal-inspections:createInspectionFromTemplate.unitTypes')}
                      name="locationType"
                      value={LOCATION_TYPES.UNIT_TYPES}
                      checked={values.locationType === LOCATION_TYPES.UNIT_TYPES}
                      onChange={() =>
                        onChangeLocationType(LOCATION_TYPES.UNIT_TYPES, values, setValues)
                      }
                    />
                  )}
                </div>
                {isYardi && yardiOptions.length > 0 && (
                  <SelectField
                    name="phase"
                    options={yardiOptions}
                    placeholder={t(
                      'internal-inspections:createInspectionFromTemplate.selectPhasePlaceholder',
                    )}
                    onChange={(e) => {
                      handleChange(e)
                      fetchAllUnits(e.target.value || '')
                      if (values.locationType === LOCATION_TYPES.UNIT) {
                        setFieldValue('locationValue', [])
                      }
                    }}
                    value={values.phase}
                  />
                )}
                <div className="select-location">
                  {getInspectionLocationInput(
                    values,
                    handleChange,
                    setFieldValue,
                    {
                      unitOptions,
                      unitTypes,
                    },
                    t,
                  )}
                </div>
              </div>
            </div>
            <div>
              <span className="input-label">
                {t('internal-inspections:createInspectionFromTemplate.assignTo')}{' '}
                <span className="input-label--light">
                  {t('internal-inspections:frequencyFields.optional')}
                </span>
              </span>
              <Select
                placeholder={t(
                  'internal-inspections:createInspectionFromTemplate.searchByStaffNamePlaceholder',
                )}
                className="search-select-container"
                classNamePrefix="select-primary"
                options={[...staffOptions, ...propertyManagerOptions]}
                isMulti
                closeMenuOnSelect={false}
                onChange={(items) =>
                  setFieldValue(
                    'assigneeIds',
                    items.map((item) => item.value).slice(0, MAX_STAFF_OPTIONS),
                  )
                }
                isOptionDisabled={(option) =>
                  values.assigneeIds &&
                  values.assigneeIds.length >= MAX_STAFF_OPTIONS &&
                  !values.assigneeIds.includes(option.value)
                }
              />
              <span className="input-label--desc">
                {t('internal-inspections:createInspectionFromTemplate.selectUpTo5Assignees')}
              </span>
            </div>
            {currentTrigger === TRIGGER_OPTIONS.ONE_TIME ? (
              <div>
                <span className="input-label">
                  {t('internal-inspections:createInspectionFromTemplate.scheduleDate')}
                </span>
                <DateField
                  name="date"
                  placeholderText={t(
                    'internal-inspections:createInspectionFromTemplate.dateFormatPlaceholder',
                  )}
                  autoComplete="off"
                  dateFormat="MM/dd/yyyy"
                  minDate={new Date()}
                  value={values.date}
                  onChange={handleChange}
                  onChangeRaw={(e) => e.preventDefault()}
                />
              </div>
            ) : (
              <React.Fragment>
                <div>
                  <span className="input-label">
                    {t('internal-inspections:createInspectionFromTemplate.setFrequency')}
                  </span>
                  <div className="radio-buttons-container">
                    <div className="radio-buttons">
                      <RadioField
                        label={t('internal-inspections:createInspectionFromTemplate.timeBased')}
                        name="frequency"
                        value={TRIGGER_TYPES.TIME}
                        checked={values.frequency === TRIGGER_TYPES.TIME}
                        onChange={(e) => {
                          setValues({
                            ...values,
                            recurrenceType: 'weekly',
                            date: null,
                            frequency: e.target.value,
                          })
                        }}
                      />
                      <RadioField
                        label={t('internal-inspections:createInspectionFromTemplate.triggerBased')}
                        name="frequency"
                        value={TRIGGER_TYPES.TRIGGER}
                        checked={values.frequency === TRIGGER_TYPES.TRIGGER}
                        onChange={(e) => {
                          setValues({
                            ...values,
                            recurrenceType: TRIGGER_TYPES.TRIGGER,
                            dayOfWeek: null,
                            recurrenceEndDate: null,
                            frequency: e.target.value,
                          })
                        }}
                      />
                    </div>
                  </div>
                </div>
                {getInspectionFrequencyFields(values, handleChange, setFieldValue, {}, t)}
              </React.Fragment>
            )}
            <div>
              <CheckboxField
                name="reviewRequired"
                data-testid="reviewRequired"
                label={t(
                  'internal-inspections:createInspectionFromTemplate.requireReviewAfterCompletion',
                )}
                value={values.reviewRequired}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="inspections-modal-actions">
            <button onClick={onBack} className="button button-gray">
              {t('common:actions.back')}
            </button>
            <button
              className="button button-primary auto-width"
              disabled={!canSubmit(values) || isSubmitting}
              onClick={() =>
                getSelectedUnitsCount(values) > 1 ? setIsConfirmOpen(true) : handleSubmit()
              }
            >
              {t('internal-inspections:createInspectionFromTemplate.createInspection')}
            </button>
          </div>
          <ConfirmPopup
            isOpen={isConfirmOpen}
            cancelCallback={() => setIsConfirmOpen(false)}
            confirmCallback={() => {
              setIsConfirmOpen(false)
              handleSubmit()
            }}
            isLoading={isSubmitting}
            cancelText={t('common:actions.back')}
            confirmText={t('internal-inspections:createInspectionFromTemplate.confirmCreate')}
            title={t('internal-inspections:createInspectionFromTemplate.cautionTitle')}
            body={getMultiUnitCautionText(
              t,
              selectedTemplate ? selectedTemplate.label : '',
              getSelectedUnitsCount(values),
            )}
            className="is-caution"
          />
        </React.Fragment>
      )}
    </Formik>
  )
}

CreateInspectionFromTemplate.propTypes = {
  onClose: PropTypes.func,
  onBack: PropTypes.func,
  createInspectionFromTemplate: PropTypes.func,
  fetchScheduledInspections: PropTypes.func,
  selectedTemplate: PropTypes.object,
  currentTrigger: PropTypes.string,
  staffOptions: PropTypes.array,
  propertyManagerOptions: PropTypes.array,
  building: PropTypes.object,
}

export default connect(
  (state) => ({
    staffOptions: selectStaffOptions(state),
    propertyManagerOptions: selectPropertyManagerOptions(state),
    building: selectActiveBuilding(state),
  }),
  {
    createInspectionFromTemplate,
    fetchScheduledInspections,
  },
)(CreateInspectionFromTemplate)
